import { v5 as uuidv5 } from 'uuid';
/** Where the user is standing, from a pathname (assistOrigin or location). */ export function parseChatOrigin(pathname) {
    if (!pathname) return {
        surface: 'root'
    };
    const path = pathname.split('?')[0] ?? pathname;
    const program = path.match(/^\/delivery\/programs\/([^/]+)/);
    if (program?.[1]) return {
        surface: 'delivery_program',
        programId: program[1]
    };
    if (path === '/delivery' || path.startsWith('/delivery/')) return {
        surface: 'delivery_home'
    };
    const capture = path.match(/^\/capture\/([^/]+)/);
    if (capture?.[1]) return {
        surface: 'capture_pursuit',
        pursuitId: capture[1]
    };
    const proposal = path.match(/^\/proposal\/([^/]+)/);
    if (proposal?.[1]) return {
        surface: 'proposal',
        projectId: proposal[1]
    };
    const project = path.match(/^\/project\/([^/]+)/);
    if (project?.[1]) return {
        surface: 'assistant',
        projectId: project[1]
    };
    if (path.startsWith('/assistant')) return {
        surface: 'assistant'
    };
    return {
        surface: 'root'
    };
}
/**
 * "Open in full view" from the Assist rail. Capture stays on the pursuit
 * (not /assistant). Programs go to workroom; proposal/project to that workspace.
 */ export function expandDestination(origin, conversationId) {
    if (conversationId) {
        const id = conversationId.startsWith('shanks-') ? conversationId : `shanks-${conversationId}`;
        const program = origin?.match(/^\/delivery\/programs\/([^/?]+)/);
        return program?.[1] ? `/delivery/programs/${program[1]}/workroom?chat=${encodeURIComponent(id)}` : `/assistant/${encodeURIComponent(id)}`;
    }
    if (!origin) return '/assistant';
    const path = origin.split('?')[0] ?? origin;
    const program = path.match(/^\/delivery\/programs\/([^/]+)/);
    if (program?.[1]) return `/delivery/programs/${program[1]}/workroom`;
    const capture = path.match(/^\/capture\/([^/]+)/);
    if (capture?.[1]) return `/capture/${capture[1]}`;
    const project = path.match(/^\/project\/([^/]+)/);
    if (project?.[1]) return `/project/${project[1]}`;
    const proposal = path.match(/^\/proposal\/([^/]+)/);
    if (proposal?.[1]) return `/proposal/${proposal[1]}`;
    return '/assistant';
}
/** Stable, database-sized Assist identity for (user, surface, primary bind). */ export function assistConversationId(envelope, userId) {
    const bind = envelope.programId || envelope.pursuitId || envelope.projectId || 'none';
    // Keep existing keys that could be persisted in Pangaea's varchar(36).
    const who = (userId ?? '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64) || 'pending';
    const legacy = `assist-${envelope.surface}-${bind}-${who}`;
    if (legacy.length <= 36) return legacy;
    return uuidv5(JSON.stringify([
        'specra-assist',
        envelope.surface,
        bind,
        userId || 'pending'
    ]), uuidv5.URL);
}
/** Short composer/header chip. Empty on root / unpinned assistant. */ export function envelopeChip(envelope) {
    switch(envelope.surface){
        case 'delivery_program':
            return 'Program';
        case 'capture_pursuit':
            return 'Pursuit';
        case 'proposal':
        case 'proposal_document':
            return 'Proposal';
        case 'rfi_response':
            return 'RFI';
        case 'delivery_home':
            return 'Delivery';
        default:
            return '';
    }
}
/** JSON body for the SSE bridge (snake_case, matches express app.ts). */ export function envelopeToJson(envelope) {
    const out = {
        surface: envelope.surface
    };
    if (envelope.pursuitId) out.pursuit_id = envelope.pursuitId;
    if (envelope.projectId) out.project_id = envelope.projectId;
    if (envelope.programId) out.program_id = envelope.programId;
    if (envelope.documentId) out.document_id = envelope.documentId;
    if (envelope.volumeId) out.volume_id = envelope.volumeId;
    return out;
}
/** Fields for `new AgentTypes.ChatContext(...)`. */ export function toChatContextInit(envelope) {
    return {
        surface: envelope.surface,
        pursuitId: envelope.pursuitId ?? '',
        projectId: envelope.projectId ?? '',
        programId: envelope.programId ?? '',
        documentId: envelope.documentId ?? '',
        volumeId: envelope.volumeId ?? '',
        pins: (envelope.pins ?? []).map((p)=>({
                surface: p.surface,
                pursuitId: p.pursuitId ?? '',
                projectId: p.projectId ?? '',
                programId: p.programId ?? '',
                documentId: p.documentId ?? '',
                volumeId: p.volumeId ?? ''
            }))
    };
}
export const MAX_CHAT_PINS = 8;
/** /assistant owner-only private thread with no project bind. */ export function canShowPinPicker(opts) {
    if (!opts.pathname.startsWith('/assistant')) return false;
    if (opts.projectId) return false;
    if (!opts.isOwner || opts.isShared) return false;
    return true;
}
export function pinKey(pin) {
    return [
        pin.surface,
        pin.pursuitId,
        pin.projectId,
        pin.programId,
        pin.documentId,
        pin.volumeId
    ].filter(Boolean).join(':');
}
/**
 * Prefer the live path when it is a known desk; otherwise keep the origin
 * the rail was opened from (user navigated to a root-ish page with Assist open).
 */ export function resolveAssistPath(pathname, origin) {
    const live = parseChatOrigin(pathname);
    if (live.surface !== 'root') return pathname?.split('?')[0] ?? pathname ?? '';
    return origin || pathname || '';
}
